/** components/Button.tsx */
import { AtomButton, AtomButtonProps } from '@/atoms/button'
import { css, Interpolation, Theme } from '@emotion/react'
import { forwardRef } from 'react'

const baseStyle = (t: Theme) => css`
  border-width: 1px;
  border-style: solid;
  border-radius: 5px;
  padding: ${t.s.xxxs}px ${t.s.xxs}px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${t.s.xxxs}px;
  transition: ease-out background-color 0.3s, border-color 0.3s, outline 0.1s;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &:focus {
    outline: 2px solid ${t.c.ec.shallowBlue};
    position: relative;
  }
`
const colorVariantStyles = {
  primary: (t: Theme) => css`
    background-color: ${t.c.bc.primary};
    border-color: ${t.c.bc.primary};
    color: ${t.c.ec.white};
    &:not(:disabled):hover {
      background-color: ${t.c.ec.deepBlue};
      border-color: ${t.c.ec.deepBlue};
    }
    &:not(:disabled):active {
      background-color: ${t.c.ec.deepBlue};
    }
  `,
  accent: (t: Theme) => css`
    background-color: ${t.c.ec.accentBlue};
    border-color: ${t.c.ec.accentBlue};
    color: ${t.c.ec.white};
    &:not(:disabled):hover {
      background-color: ${t.c.ec.accentBlueHover};
      border-color: ${t.c.ec.accentBlueHover};
    }
    &:not(:disabled):active {
      background-color: ${t.c.ec.accentBlueHover};
    }
  `,
  outline: (t: Theme) => css`
    background-color: ${t.c.ec.white};
    border-color: ${t.c.bc.primary};
    color: ${t.c.bc.primary};
    &:not(:disabled):hover {
      background-color: ${t.c.bc.primary};
      color: ${t.c.ec.white};
    }
    &:not(:disabled):active {
      background-color: ${t.c.ec.deepBlue};
      border-color: ${t.c.ec.deepBlue};
      color: ${t.c.ec.white};
    }
  `,
  dangerOutline: (t: Theme) => css`
    background-color: ${t.c.ec.white};
    border-color: ${t.c.bc.danger};
    color: ${t.c.bc.danger};
    &:not(:disabled):hover {
      background-color: ${t.c.bc.danger};
      color: ${t.c.ec.white};
    }
    &:not(:disabled):active {
      background-color: ${t.c.ec.deepRed};
      border-color: ${t.c.ec.deepRed};
      color: ${t.c.ec.white};
    }
  `,
  accentOutline: (t: Theme) => css`
    background-color: ${t.c.ec.white};
    border-color: ${t.c.ec.accentBlue};
    color: ${t.c.ec.accentBlue};
    &:not(:disabled):hover {
      background-color: ${t.c.ec.accentBlue};
      color: ${t.c.ec.white};
    }
    &:not(:disabled):active {
      background-color: ${t.c.ec.accentBlueHover};
      border-color: ${t.c.ec.accentBlueHover};
      color: ${t.c.ec.white};
    }
  `,
  danger: (t: Theme) => css`
    background-color: ${t.c.bc.danger};
    border-color: ${t.c.bc.danger};
    color: ${t.c.ec.white};
    &:not(:disabled):hover {
      background-color: ${t.c.ec.deepRed};
      border-color: ${t.c.ec.deepRed};
    }
    &:not(:disabled):active {
      background-color: ${t.c.ec.deepRed};
    }
  `,
  normal: (t: Theme) => css`
    background-color: #ffffff;
    border-color: #bdbdbd;
    color: #454545;
    &:not(:disabled):hover {
      background-color: #f5f5f5;
    }
    &:not(:disabled):active {
      background-color: #eeeeee;
    }
  `,
}

const sizeVariantStyles = {
  sm: (t: Theme) => css`
    font-size: ${t.fs.sm};
    line-height: ${t.fs.sm};
  `,
  md: (t: Theme) => css`
    font-size: ${t.fs.md};
    line-height: ${t.fs.md};
  `,
  lg: (t: Theme) => css`
    font-size: ${t.fs.lg};
    line-height: ${t.fs.lg};
  `,
  fixed38: (t: Theme) => css`
    font-size: ${t.fs.md};
    line-height: ${t.fs.md};
    min-height: 38px;
    padding-top: 0.625rem;
    padding-bottom: 0.625rem;
    box-sizing: border-box;
  `,
}

export type ColorVariant = keyof typeof colorVariantStyles
export type SizeVariant = keyof typeof sizeVariantStyles

export type Props = {
  type?: 'button' | 'submit' | 'reset'
  color?: ColorVariant
  size?: SizeVariant
  addStyle?: Interpolation<Theme>
} & Omit<AtomButtonProps, 'type' | 'value'>

export const BaseButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      children,
      type = 'button',
      color = 'primary',
      size = 'sm',
      addStyle,
      ...attrs
    }: Props,
    forwardedRef,
  ) => (
    <AtomButton
      type={type}
      css={[
        baseStyle,
        colorVariantStyles[color],
        sizeVariantStyles[size],
        addStyle,
      ]}
      ref={forwardedRef}
      {...attrs}
    >
      {children}
    </AtomButton>
  ),
)
